import Vue from 'vue'
import Vuex from 'vuex'
import ChannelAPI, { EventTypes } from '../API/ChannelAPI'
import { getCookies } from '../utils/cookieUtils'
import { immediateInterval } from '../../../common/utils/immediateInterval'
import { knuthShuffle } from 'knuth-shuffle'
import { objectToQueryString } from '@/common/utils/queryStringUtils'
import { findIndex, find, pick } from 'lodash'
import {
  ProjectActivity,
  ProjectRow,
  ProjectQueryStringParams,
  Project,
  ProjectState,
  ProjectPath,
  RowTileType,
  ViewerHistoryEntry,
  RedirectUrlActivityFlag,
  RedirectUrlChannelFlag,
  RedirectUrlChannelActivitiesPlayedFlag,
  RedirectUrlChannelTilesClickedFlag,
  AppendToPassbackKind,
} from '../channel.types'
import getEnvVars from '@/common/utils/getEnvVars'
import injectCSS from '@/common/utils/injectCSS'
import { api } from '../API/ChannelAPI'

const SocialLinks = {
  twitter: {
    id: 'twitter',
    iconSrc: require('../assets/icon-twitter.svg'),
    title: 'Share on Twitter',
  },
  facebook: {
    id: 'facebook',
    iconSrc: require('../assets/icon-facebook.svg'),
    title: 'Share on Facebook',
  },
}

function payloadFromStateOrPath(state, path) {
  const expandedActivity = path ? path.activity : state.selection.activity
  const expandedRow = path ? path.row : state.selection.row
  const rowIndex = path ? path.row_index : state.selection.row_index

  return {
    id: expandedActivity.id,
    activityId: expandedActivity.activity_id,
    activityDisplayStateId: expandedActivity.display_state_id,
    categoryId: expandedActivity.category_id,
    displayPosition: expandedActivity.display_position,
    categoryDisplayPosition: rowIndex,
  }
}

function createProjectStore(
  queryStringParams: ProjectQueryStringParams,
  apiHost: string,
  rawProject?: Project
) {
  const searchFilter = (search_query_regex: RegExp | null) => (
    activity: ProjectActivity
  ) => {
    if (!search_query_regex) {
      return true
    }
    return (
      search_query_regex.test(activity.name) ||
      //description is not always present, and passing null gets coerced to string.
      search_query_regex.test(activity.description || '') ||
      search_query_regex.test(activity.synopsis)

      // TODO: Add search within row names
    )
  }

  return new Vuex.Store<ProjectState>({
    strict: getEnvVars().NODE_ENV === 'development',

    state: {
      channel_url: window.location.href,
      project: rawProject,
      participant_id: queryStringParams.participant_id || null,
      api_host: apiHost,
      viewer_key: undefined,
      favorites: [],
      viewer_history: [],
      timer: undefined,
      search_query: null,
      search_query_regex: null,
      selection: null,
      played_activity: null,
      description: '',
      article_engagements: {
        engagement_types: null,
        article_loading: true,
        emojis: {
          active_emoji_id: null,
          list: null,
        },
      },

      auth_token: null,
      numberOfChannelActions: 0,
      numberOfActivityActions: 0,
      activitiesPlayed: [],
      tilesClicked: [],
    },

    getters: {
      returnUrl: (state, { cookies }) => {
        const return_url =
          decodeURIComponent(queryStringParams.return_url || '') ||
          decodeURIComponent(cookies.TIMER_REDIRECT_COOKIE.get())
        return return_url
      },

      cookies: state => getCookies(state.project.project_guid),

      fullActivityUrl: state => path => {
        const activityUrl = path.activity.url
        return (
          `/a/${activityUrl}` +
          objectToQueryString({
            return_url: location.href,
            participant_id: state.participant_id,
            channel_viewer_key: state.viewer_key,
            channel: state.project.project_guid,
            autoplay: true,
          })
        )
      },

      portalName: state => path => {
        return `portal-${path.row.id}`
      },

      searchResults: ({ project, search_query_regex }) =>
        project.activities.filter(searchFilter(search_query_regex)),

      activeRows: ({ project }) =>
        project.rows
          .filter(({ is_active }) => is_active)
          .sort((a, b) => a.display_order - b.display_order)
          .map(
            ({
              id,
              type,
              row_tile_type,
              layout_class,
              layout,
              name,
              html_code,
              is_enabled_title_display,
              is_enabled_article_format,
              is_enabled_activity_clickthrough,
              is_enabled_pagination_controls,
              is_enabled_second_screen,
              autoplay_speed,
            }) => ({
              id,
              type,
              row_tile_type,
              layout_class,
              layout,
              name,
              html_code,
              is_enabled_article_format,
              is_enabled_title_display,
              is_enabled_activity_clickthrough,
              is_enabled_pagination_controls,
              is_enabled_second_screen,
              autoplay_speed,
            })
          ),

      categories: (state, getters) => {
        return getters.activeRows.filter(({ type }) => type === 'TILES')
      },

      activities: ({ project, favorites, viewer_history }) => (
        row_tile_type: RowTileType,
        category_id
      ) => {
        let filteredActivities: ProjectActivity[] = []
        const shuffleActivities = ((randomize: boolean) => activities =>
          randomize ? knuthShuffle(activities) : activities)(
          project.is_enabled_tile_order_randomization
        )
        switch (row_tile_type) {
          case 'category':
            filteredActivities = shuffleActivities(
              project.activities.filter(({ categories }) =>
                categories.find(cid => cid === category_id)
              )
            )
            break
          case 'dynamic_favorites':
            filteredActivities = favorites.map(
              activity_id =>
                project.activities.find(a => a.activity_id === activity_id)!
            )
            break
          case 'dynamic_history':
            filteredActivities = viewer_history.map(
              ({ activity_id }) =>
                project.activities.find(a => a.activity_id === activity_id)!
            )
            break
          case 'dynamic_popular':
            filteredActivities = project.popular_activities
              .map(({ activity_id }) =>
                project.activities.find(a => a.activity_id === activity_id)
              )
              .filter(a => typeof a !== 'undefined') as ProjectActivity[]

            break
        }

        return filteredActivities
      },
      isFavorite: ({ favorites }) => path => {
        return favorites.indexOf(path.activity.activity_id) > -1
      },
      isPathSelected: ({ selection }) => path => {
        return (
          selection &&
          path &&
          selection.activity &&
          selection.row &&
          path.activity &&
          path.row &&
          selection.activity.activity_id == path.activity.activity_id &&
          selection.row.id == path.row.id
        )
      },
      isSelectedPathAnArticle: ({ selection }) => {
        if (selection && selection.activity && selection.row) {
          return selection.activity && selection.row.is_enabled_article_format
        } else {
          return false
        }
      },
      highlightedActivities: state =>
        state.project.activities.filter(({ is_highlighted }) => is_highlighted),

      orderedLinks: state =>
        [...state.project.links].sort((a, b) => a.order - b.order),

      socialLinks: ({ project }) =>
        Object.keys(project.social_links)
          .filter(name => !!project.social_links[name])
          .map(name => ({
            ...SocialLinks[name],
            url: project.social_links[name],
          })),

      button: ({ project }) => type =>
        ({
          favorite: {
            label: project.button_1_label,
            icon: project.button_1_icon,
          },
          share: {
            label: project.button_2_label,
            icon: project.button_2_icon,
          },
        }[type]),
    },

    mutations: {
      //{prop1, type2, value}
      setArticleEngagement(state, { prop1, prop2, value }) {
        if (!prop1 && !prop2) {
          throw new Error(
            'setArticleEngagement: neither prop1 nor prop2 are defined'
          )
        } else if (!prop1 && prop2) {
          throw new Error(
            'setArticleEngagement: neither prop2 is defined, but prop1 is not'
          )
        }

        if (prop2) {
          state.article_engagements[prop1][prop2] = value
        } else {
          state.article_engagements[prop1] = value
        }
      },

      set_active_emoji_id(state, id) {
        state.article_engagements.emojis.active_emoji_id = id
      },

      setChannel(state, channel) {
        state.project = channel
      },
      updateViewerKey(state, viewer_key) {
        state.viewer_key = viewer_key
      },

      setFavorites(state, favorites: number[]) {
        state.favorites = favorites
      },

      setViewerHistory(state, viewer_history: ViewerHistoryEntry[]) {
        state.viewer_history = viewer_history
      },

      updateTimer(state, remaining_seconds: number) {
        state.timer = {
          state: remaining_seconds > 0 ? 'running' : 'finished',
          remaining_seconds: remaining_seconds > 0 ? remaining_seconds : 0,
        }
      },

      setPath(state, path: ProjectPath) {
        state.selection = path
      },

      setSelectedActivityFullDescription(state, fullDescription) {
        if (state.selection) {
          state.selection.activity.description = fullDescription
        }
      },

      setPlayedActivity(state, url: string | null) {
        state.played_activity = url
      },

      updateSearchQuery(state, search_query: string) {
        state.search_query = search_query || null
        state.search_query_regex =
          state.search_query !== null
            ? new RegExp(
                search_query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'),
                'i'
              )
            : null
      },

      setAuthToken(state, authToken: string) {
        state.auth_token = authToken
      },
      incrementChannelActions(state) {
        state.numberOfChannelActions++
      },

      incrementActivityActions(state) {
        state.numberOfActivityActions++
      },

      appendActivityIdToPassback(
        state,
        { activityId, kind }: { activityId: string; kind: AppendToPassbackKind }
      ) {
        let arr =
          kind == AppendToPassbackKind.ACTIVITIES_PLAYED
            ? state.activitiesPlayed
            : state.tilesClicked
        let found = false

        for (let i = 0; i < arr.length; i++) {
          let playedActivityId = arr[i]
          if (playedActivityId == activityId) {
            found = true
            break
          }
        }

        if (!found) {
          if (kind == AppendToPassbackKind.ACTIVITIES_PLAYED) {
            state.activitiesPlayed.push(activityId)
          } else if (kind == AppendToPassbackKind.TILES_CLICKED) {
            state.tilesClicked.push(activityId)
          }
        }
      },
    },

    actions: {
      async get_channel({ commit, state }, slug) {
        const channel = await ChannelAPI.getChannel(slug)
        this.commit('setChannel', channel)
        return channel
      },

      inject_css({ state }) {
        const { project } = state
        injectCSS(`
          :root {
              --primary-color: ${project.primary_color};
              --contrast-color: ${project.contrast_color};
          }
      `)
        injectCSS(project.custom_css)
      },

      async init({ commit, state, getters }, slug) {
        await this.dispatch('get_channel', slug)

        document.title = state.project.name || 'Lumière'

        const { CHANNEL_VIEWER_COOKIE, TIMER_REDIRECT_COOKIE } = getters.cookies
        if (queryStringParams.reset_session) {
          CHANNEL_VIEWER_COOKIE.remove()
        }
        const viewerKey =
          CHANNEL_VIEWER_COOKIE.get() ||
          (await ChannelAPI.getViewerKey(state.participant_id))
        CHANNEL_VIEWER_COOKIE.set(viewerKey)

        commit('updateViewerKey', viewerKey)

        if (queryStringParams.return_url && !TIMER_REDIRECT_COOKIE.get()) {
          TIMER_REDIRECT_COOKIE.set(queryStringParams.return_url)
        }
      },

      async init_timer({ commit, state, getters }) {
        const { min_view_time } = state.project
        const { TIMER_COOKIE, TIMER_REDIRECT_COOKIE } = getters.cookies
        if (min_view_time) {
          if (
            queryStringParams.reset_session ||
            queryStringParams.reset_timer
          ) {
            TIMER_COOKIE.remove()
            TIMER_REDIRECT_COOKIE.remove()
          }

          const expiration_time = Number(
            TIMER_COOKIE.get() || new Date().getTime() + min_view_time * 1000
          )

          TIMER_COOKIE.set(String(expiration_time))

          const stopTimer = immediateInterval(() => {
            const remaining_time = expiration_time - new Date().getTime()
            commit('updateTimer', Math.floor(remaining_time / 1000))

            if (remaining_time <= 0) {
              stopTimer()
            }
          }, 1000)
        }
      },

      async handle_link_click({ getters }, link) {
        await api.trackEvent({
          eventType: EventTypes.link,
          linkId: link.id,
        })
        location.href = link.url
      },

      async track_category_click({ getters }, { categoryId }) {
        await api.trackEvent({
          eventType: EventTypes.category,
          categoryId,
        })
      },

      async handle_timer_button_click({ getters, state }) {
        if (this.getters.returnUrl) {
          let numberOfActivityActions = state.numberOfActivityActions
          let numberOfChannelActions = state.numberOfChannelActions
          let activitiesPlayed = state.activitiesPlayed.join(',')
          let tilesClicked = state.tilesClicked.join(',')

          let returnUrl = this.getters.returnUrl

          let regexActivity = new RegExp(RedirectUrlActivityFlag)
          let regexChannel = new RegExp(RedirectUrlChannelFlag)
          let regexChannelActivitiesPlayed = new RegExp(
            RedirectUrlChannelActivitiesPlayedFlag
          )
          let regexChannelTilesClicked = new RegExp(
            RedirectUrlChannelTilesClickedFlag
          )

          if (returnUrl.match(regexActivity)) {
            returnUrl = returnUrl.replace(
              regexActivity,
              '' + numberOfActivityActions
            )
          }

          if (returnUrl.match(regexChannel)) {
            returnUrl = returnUrl.replace(
              regexChannel,
              '' + numberOfChannelActions
            )
          }

          if (returnUrl.match(regexChannelActivitiesPlayed)) {
            returnUrl = returnUrl.replace(
              regexChannelActivitiesPlayed,
              '' + activitiesPlayed
            )
          }

          if (returnUrl.match(regexChannelTilesClicked)) {
            returnUrl = returnUrl.replace(
              regexChannelTilesClicked,
              '' + tilesClicked
            )
          }

          location.href = returnUrl
        }
      },

      async handle_favorite_click(
        { getters, dispatch, commit, state },
        path: ProjectPath
      ) {
        const activityId = path
          ? path.activity.activity_id
          : state.selection.activity.activity_id
        const indexInFavorites = state.favorites.indexOf(activityId)
        const isFavorite = indexInFavorites === -1

        await api.trackEvent({
          eventType: EventTypes.favorite,
          ...payloadFromStateOrPath(state, path),
          isFavorite,
        })

        const newFavorites = isFavorite
          ? [...state.favorites, activityId]
          : state.favorites
              .slice(0, indexInFavorites)
              .concat(state.favorites.slice(indexInFavorites + 1))

        commit('setFavorites', newFavorites)
        dispatch('fetch_viewer_data')
      },

      async track_share_click({ state, getters }, path: ProjectPath = null) {
        await api.trackEvent({
          eventType: EventTypes.share,
          ...payloadFromStateOrPath(state, path),
        })
      },

      async track_article_view({ state, getters }) {
        await api.trackEvent({
          eventType: EventTypes.read,
          ...payloadFromStateOrPath(state, null),
        })
      },

      async track_article_link_click({ state, getters }, link) {
        await api.trackEvent({
          eventType: EventTypes.click_article_link,
          link,
          ...payloadFromStateOrPath(state, null),
        })
      },

      async handle_activity_play_click(
        { commit, getters, state, dispatch },
        path: ProjectPath = null
      ) {
        const pathObj: ProjectPath = path || state.selection

        const activity = pathObj.activity
        const row = pathObj.row
        const activityId = activity.activity_id

        await api.trackEvent({
          eventType: EventTypes.activity,
          ...payloadFromStateOrPath(state, path),
        })

        if (
          row.is_enabled_activity_clickthrough ||
          row.type == 'HERO_CAROUSEL'
        ) {
          commit('setPlayedActivity', getters.fullActivityUrl(pathObj))
        }

        if (
          !state.viewer_history.some(
            ({ activity_id }) => activity_id === activityId
          )
        ) {
          commit('setViewerHistory', [
            ...state.viewer_history,
            { activity_id: activityId },
          ])
          // dispatch('fetch_viewer_data')
        }
      },

      async handle_hero_init_description_and_emojis(
        { commit, getters, state, dispatch },
        activity
      ) {
        if (!activity.description) {
          let id = activity.id
          let projectId = this.state.project.id
          let displayStateId = activity.display_state_id

          let data = await api.getArticleTextAndEngagements(
            id,
            projectId,
            displayStateId
          )

          return data
        }
      },

      async handle_expand_click({ commit, getters, state, dispatch }) {
        const path: ProjectPath = state.selection

        if (!path.activity.description) {
          let fullDescription = await api.getPathDetails(state.project.id, path)
        }

        let activity = this.state.selection.activity
        let id = activity.id
        let projectId = this.state.project.id
        let displayStateId = activity.display_state_id

        await dispatch('get_article_text_and_engagements', {
          id,
          projectId,
          displayStateId,
        })

        await api.trackEvent({
          eventType: EventTypes.expand,
          ...payloadFromStateOrPath(state, null),
        })

        let { auth_token } = await api.authenticate(state.selection)

        commit('setAuthToken', auth_token)
      },

      async handle_collapse_click({ commit, dispatch, getters, state }) {
        await api.trackEvent({
          eventType: EventTypes.collapse,
          ...payloadFromStateOrPath(state, null),
        })

        commit('setPath', null)
      },

      async track_visit({ getters }) {
        await api.trackEvent({
          eventType: EventTypes.visit,
        })
      },

      //interactionData will be {hotspotId, hotspotValue}
      async article_interaction({ commit, getters, state }, interactionData) {
        await api.trackEvent({
          eventType: EventTypes.hotspot_emoji,
          ...payloadFromStateOrPath(state, null),
          ...interactionData,
          viewerKey: state.viewer_key,
          projectId: state.project.id,
        })
      },

      async get_article_text_and_engagements(
        { commit, getters },
        { id, projectId, displayStateId, activity }
      ) {
        commit('setArticleEngagement', {
          prop1: 'article_loading',
          value: true,
        })

        let data = await api.getArticleTextAndEngagements(
          id,
          projectId,
          displayStateId
        )

        let articleText = data['description']

        commit('setSelectedActivityFullDescription', articleText)

        let emojis = data['emojis'] || null
        let engagementTypes = {}

        //This is where we can accommodate many different kinds of
        //acticle interactions
        if (emojis) {
          engagementTypes['emojis'] = true

          commit('setArticleEngagement', {
            prop1: 'emojis',
            prop2: 'list',
            value: emojis,
          })
        }

        commit('setArticleEngagement', {
          prop1: 'engagement_types',
          value: engagementTypes,
        })

        commit('setArticleEngagement', {
          prop1: 'article_loading',
          value: false,
        })
      },

      async fetch_viewer_data({ commit, getters }) {
        const { viewer_history, favorites } = await api.getViewerData()

        commit('setFavorites', favorites)
        commit('setViewerHistory', viewer_history)
      },

      async enter_search({ commit, getters }, query) {
        commit('updateSearchQuery', query)
      },

      async reset_search({ commit }) {
        commit('updateSearchQuery', null)
      },
    },
  })
}

export default createProjectStore
